import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MessageCircle, Instagram } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-obsidian border border-white/15 px-5 py-4 text-white placeholder:text-white/30 focus:border-volt focus:outline-none transition-colors duration-300";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", location: "", position: "", message: "" });
  const [sending, setSending] = useState(false);

  const CONTACT_LINKS = [
    { icon: Mail, label: "twoshorescollegiate@gmail.com", href: "mailto:twoshorescollegiate@gmail.com", testid: "contact-email-link" },
    { icon: MessageCircle, label: t("contact.whatsapp"), href: "https://wa.me/33778323015", testid: "contact-whatsapp-link" },
    { icon: Instagram, label: "@twoshorescollegiate", href: "https://www.instagram.com/twoshorescollegiate", testid: "contact-instagram-link" },
  ];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, lang: localStorage.getItem("tsc_lang") === "fr" ? "fr" : "en" });
      toast.success(t("contact.ok"));
      setForm({ name: "", email: "", location: "", position: "", message: "" });
    } catch {
      toast.error(t("contact.err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40" data-testid="contact-section">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
        <Reveal>
          <p className="font-jetbrains text-xs uppercase tracking-[0.35em] text-volt mb-6">{t("contact.chapter")}</p>
          <h2 className="font-cabinet font-black uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight mb-8">
            {t("contact.h2a")}<span className="text-volt">{t("contact.h2b")}</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md mb-12">
            {t("contact.copy")}
          </p>
          <div className="space-y-1">
            {CONTACT_LINKS.map((l) => (
              <a
                key={l.testid}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={l.testid}
                className="group flex items-center justify-between border-t border-white/10 py-5 hover:border-volt/50 transition-colors duration-300"
              >
                <span className="flex items-center gap-4 text-white/80 group-hover:text-volt transition-colors duration-300">
                  <l.icon size={18} />
                  <span className="text-base md:text-lg">{l.label}</span>
                </span>
                <ArrowUpRight size={18} className="text-white/30 group-hover:text-volt group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-surface border border-white/10 p-8 md:p-12 space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contact-name" className="block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{t("contact.name")}</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder={t("contact.namePh")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{t("contact.email")}</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contact-location" className="block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{t("contact.location")}</label>
                <input id="contact-location" data-testid="contact-location-input" value={form.location} onChange={set("location")} placeholder={t("contact.locationPh")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-position" className="block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{t("contact.position")}</label>
                <select id="contact-position" data-testid="contact-position-select" value={form.position} onChange={set("position")} className={inputCls}>
                  <option value="">{t("contact.select")}</option>
                  <option value="Goalkeeper">{t("contact.posGk")}</option>
                  <option value="Defender">{t("contact.posDf")}</option>
                  <option value="Midfielder">{t("contact.posMf")}</option>
                  <option value="Forward / Winger">{t("contact.posFw")}</option>
                  <option value="Other">{t("contact.posOther")}</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="contact-message" className="block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{t("contact.message")}</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder={t("contact.messagePh")} className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group w-full rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 flex items-center justify-center gap-2 hover:bg-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? t("contact.sending") : t("contact.send")}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
